import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from "react";
import "./CommonTour.css";
import { Button } from "../utils";
import { buttonClasses } from "../utils/theme";
import { useUpdateTourStatusMutation } from "../../api/userSlice";
import { useUserDetail } from "../../services/hooks";

const CommonTour = forwardRef(({ steps = [], moduleName, onTourComplete }, ref) => {
	const { userDetail, refetch } = useUserDetail();
	const [currentStep, setCurrentStep] = useState(0);
	const [isTourRunning, setIsTourRunning] = useState(false);
	const [animate, setAnimate] = useState(false);
	const [targetRect, setTargetRect] = useState(null);
	const [updateTour, { isLoading: isUpdating, data }] = useUpdateTourStatusMutation();
	const shouldRunTour = !userDetail?.moduleTour?.[moduleName];
	// Expose restartTour via ref
	useImperativeHandle(ref, () => ({
		restartTour: () => {
			startTour();
			setIsTourRunning(true);
		},
	}));
	const startTour = () => {
		if (userDetail?.receivedGuidedTour === true) {
			if (shouldRunTour) {
				setIsTourRunning(true);
				setCurrentStep(0);
			}
		}
	};
	const checkTargetExists = (selector) => {
		return document.querySelector(selector) !== null;
	};
	useEffect(() => {
		if (!moduleName && userDetail?.receivedGuidedTour === false) {
			startTour();
			return;
		}
		if (moduleName && userDetail?.receivedGuidedTour && !userDetail?.moduleTour?.[moduleName]) {
			startTour();
		}
	}, [userDetail, moduleName]);

	const getLastValidStepIndex = () => {
		let lastIndex = steps.length - 1;
		while (lastIndex >= 0 && !checkTargetExists(steps[lastIndex]?.target)) {
			lastIndex--;
		}
		return lastIndex;
	};
	const handleNext = async () => {
		// Execute current step's action if it exists
		if (typeof steps[currentStep]?.action === "function") {
			try {
				await steps[currentStep].action();
				await new Promise((resolve) => setTimeout(resolve, 500));
			} catch (error) {
				console.error("Tour action failed:", error);
			}
		}

		// Find next valid step
		let nextStep = currentStep;
		do {
			nextStep++;
			if (nextStep >= steps.length) {
				await handleFinish();
				return;
			}
		} while (!checkTargetExists(steps[nextStep]?.target) && nextStep < steps.length);

		setCurrentStep(nextStep);
	};

	const handlePrev = () => {
		// Skip steps where target doesn't exist when going back
		let prevStep = currentStep;
		do {
			prevStep--;
			if (prevStep < 0) return;
		} while (!checkTargetExists(steps[prevStep]?.target) && prevStep >= 0);

		setCurrentStep(prevStep);
	};
	const handleFinish = async () => {
		await completeTour();
		setCurrentStep(0);
	};

	const handleSkip = async () => {
		await completeTour();
		setCurrentStep(0);
	};

	const completeTour = async () => {
		setIsTourRunning(false);
		try {
			if (moduleName) {
				await updateTour({
					moduleTour: { [moduleName]: true },
					userId: userDetail?.userId,
				}).unwrap();
				// Refresh user details to get the updated tour status
				await refetch();

				if (onTourComplete) {
					onTourComplete();
				}
			}
		} catch (error) {
			console.error("Failed to update tour status:", error);
		}
	};

	// Auto start on first visit as per the final layout of the screen
	useLayoutEffect(() => {
		if (shouldRunTour) {
			startTour();
		}
	}, [shouldRunTour]);

	useEffect(() => {
		let prevTop = 0;
		let prevLeft = 0;
		let intervalId;

		if (isTourRunning && steps[currentStep]) {
			intervalId = setInterval(() => {
				const el = document.querySelector(steps[currentStep]?.target);
				if (el) {
					const rect = el.getBoundingClientRect();
					const absTop = rect.top + window.scrollY;
					const absLeft = rect.left + window.scrollX;

					// Update only if position actually changed
					if (absTop !== prevTop || absLeft !== prevLeft) {
						prevTop = absTop;
						prevLeft = absLeft;
						setTargetRect({
							width: rect.width,
							height: rect.height,
							top: absTop,
							left: absLeft,
						});
					}
				}
			}, 100); // Poll every 100ms
		}

		return () => clearInterval(intervalId);
	}, [isTourRunning, currentStep, steps]);

	// Animate on step change
	useEffect(() => {
		setAnimate(false);
		const timer = setTimeout(() => setAnimate(true), 10);
		return () => clearTimeout(timer);
	}, [currentStep]);

	// Update target position on step change or when window scroll/resize events occur.
	useEffect(() => {
		const updateTargetPosition = () => {
			const el = document.querySelector(steps[currentStep]?.target);
			if (el) {
				const rect = el.getBoundingClientRect();
				setTargetRect({
					width: rect.width,
					height: rect.height,
					top: rect.top + window.scrollY,
					left: rect.left + window.scrollX,
				});
			}
		};

		if (steps.length > 0) {
			updateTargetPosition();
		}

		// Update position on scroll and resize
		const handleScrollResize = () => {
			updateTargetPosition();
		};

		// Debounce scroll/resize events to optimize performance
		const debouncedUpdate = debounce(handleScrollResize);

		window.addEventListener("scroll", debouncedUpdate, true);
		window.addEventListener("resize", debouncedUpdate);

		return () => {
			window.removeEventListener("scroll", debouncedUpdate, true);
			window.removeEventListener("resize", debouncedUpdate);
		};
	}, [currentStep, steps]);

	useEffect(() => {
		if (!isTourRunning || currentStep >= steps.length) return;
		if (!checkTargetExists(steps[currentStep]?.target)) return;
		const { target, placement } = steps[currentStep];
		const el = document.querySelector(target);
		if (!el) return;

		const scrollAndEnsureTooltipVisibility = () => {
			const rect = el.getBoundingClientRect();
			const highlightTop = rect.top + window.scrollY;
			const highlightLeft = rect.left + window.scrollX;
			const highlightHeight = rect.height;
			const highlightWidth = rect.width;

			// Estimate tooltip dimensions
			const tooltipWidth = 300; // max width as defined in CSS
			const tooltipHeight = 120; // safe default height
			let tooltipTop = 0;

			switch (placement) {
				case "top":
					tooltipTop = highlightTop - tooltipHeight - 30;
					break;
				case "bottom":
					tooltipTop = highlightTop + highlightHeight + 30;
					break;
				case "left":
				case "right":
					tooltipTop = highlightTop + highlightHeight / 2 - tooltipHeight / 2;
					break;
				default:
					tooltipTop = highlightTop + highlightHeight + 30;
			}

			// Determine if tooltip is off-screen
			const tooltipBottom = tooltipTop + tooltipHeight;
			const viewportTop = window.scrollY;
			const viewportBottom = window.scrollY + window.innerHeight;

			if (tooltipTop < viewportTop || tooltipBottom > viewportBottom) {
				const scrollTarget = tooltipTop - 30;
				window.scrollTo({ top: Math.max(scrollTarget, 0), behavior: "smooth" });
			}
		};

		// Scroll target into view first
		el.scrollIntoView({ behavior: "instant", block: "end" });

		// Wait for layout updates, then adjust for tooltip
		setTimeout(scrollAndEnsureTooltipVisibility, 400);
	}, [currentStep, isTourRunning, steps]);
	// Instead of returning null when targetRect is missing, use a safe fallback.
	const safeTargetRect = targetRect || { top: 0, left: 0, width: 0, height: 0 };

	if (!isTourRunning || currentStep >= steps.length || !safeTargetRect.width || !safeTargetRect.height) return null;

	const { content, placement } = steps[currentStep];

	const getTooltipPosition = () => {
		const positions = {
			top: {
				top: safeTargetRect.top,
				left: safeTargetRect.left + safeTargetRect.width / 2,
				transform: "translate(-50%, calc(-100% - 30px))",
			},
			bottom: {
				top: safeTargetRect.top + safeTargetRect.height,
				left: safeTargetRect.left + safeTargetRect.width / 2,
				transform: "translate(-50%, 30px)",
			},
			left: {
				top: safeTargetRect.top + safeTargetRect.height / 2,
				left: safeTargetRect.left,
				transform: "translate(calc(-100% - 30px), -50%)",
			},
			right: {
				top: safeTargetRect.top + safeTargetRect.height / 2,
				left: safeTargetRect.left + safeTargetRect.width,
				transform: "translate(30px, -50%)",
			},
		};

		return positions[placement] || positions.bottom;
	};

	const tooltipPosition = getTooltipPosition();
	const lastValidStepIndex = getLastValidStepIndex();
	const isLastVisibleStep = currentStep === lastValidStepIndex;
	return (
		<>
			<div
				className="tour-highlight"
				style={{
					position: "absolute",
					width: `${safeTargetRect.width}px`,
					height: `${safeTargetRect.height}px`,
					top: `${safeTargetRect.top}px`,
					left: `${safeTargetRect.left}px`,
					boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
					zIndex: 999,
					pointerEvents: "none",
					borderRadius: "8px",
					border: "2px solid var(--bs-border-color)",
				}}
			/>
			<div className="tour-overlay" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
			<div
				className={`tour-tooltip ${animate ? "fade-in tooltip-move" : ""}`}
				data-placement={placement}
				style={{
					position: "absolute",
					zIndex: 1000,
					backgroundColor: "var(--background-color)",
					color: "var(--text-color)",
					padding: "15px",
					borderRadius: "8px",
					boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
					maxWidth: "300px",
					top: `${tooltipPosition.top}px`,
					left: `${tooltipPosition.left}px`,
					transform: tooltipPosition.transform,
				}}
			>
				<div className="tour-contentt">{content}</div>
				<div className="tour-buttons" style={{ display: "flex", justifyContent: "space-between", marginTop: "15px" }}>
					<div style={{ display: "flex" }}>
						{currentStep > 0 && (
							<div style={{ maxWidth: "100px", marginRight: "0.5rem" }}>
								<Button text="Prev" onClick={handlePrev} style={buttonClasses.lynkitOrangeEmpty} />
							</div>
						)}
						<div style={{ maxWidth: "100px" }}>
							<Button text={isLastVisibleStep ? "Finish" : "Next"} onClick={handleNext} style={buttonClasses.lynkitOrangeFill} />
						</div>
					</div>
					<div style={{ width: "80px", marginLeft: "5px" }}>
						<Button text="Skip Tour" onClick={handleSkip} style={buttonClasses.lynkitOrangeEmpty} />
					</div>
				</div>
			</div>
		</>
	);
});

// Debounce function to reduce excessive scroll/resize events
function debounce(func, delay) {
	let timeout;
	return function () {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, arguments), delay);
	};
}

export default CommonTour;

export const TourIcon = () => {
	return (
		<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 512 512" height={24} width={24} style={{ marginLeft: "8px", cursor: "pointer", fill: "var(--set-icon-color)", }} title="Replay Tour">
			<g>
				<g>
					<g>
						<path d="M256,0C114.616,0,0,114.616,0,256s114.616,256,256,256c141.394,0,256-114.616,256-256S397.394,0,256,0z M256,460.8 c-112.927,0-204.8-91.873-204.8-204.8S143.073,51.2,256,51.2S460.8,143.073,460.8,256S368.927,460.8,256,460.8z" />
						<path d="M349.112,238.08l-124.15-71.68c-17.07-9.851-31.037-1.792-31.037,17.92v143.36c0,19.712,13.967,27.781,31.037,17.92 l124.15-71.68C366.182,264.069,366.182,247.931,349.112,238.08z" />
					</g>
				</g>
			</g>
		</svg>
	);
};
